"use client";

import { useState } from "react";
import {
  Checkbox,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { useToolUi } from "@/components/tools/tool-ui";
import { WORKSHEET_COMMON_STRINGS } from "@/components/tools/worksheets-math/common-strings";
import { HundredsGrid } from "@/components/tools/worksheets-math/hundreds-grid";
import {
  ActionBar,
  defaultSettings,
  useCommonSettings,
  useWorksheetSeed,
  WorksheetSheets,
} from "@/components/tools/worksheets-math/worksheet-kit";
import { t } from "@/lib/i18n/interpolate";
import {
  blankNumbers,
  type HundredsRange,
  hundredsNumbers,
  type SkipMode,
  skipShaded,
} from "@/lib/tool-kit/worksheets/charts";
import { FONT_POINTS } from "@/lib/tool-kit/worksheets/layout";
import { createRng } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const HUNDREDS_CHART_STRINGS = {
  ...WORKSHEET_COMMON_STRINGS,
  titles: {
    "1-100": "Hundreds Chart",
    "1-120": "120 Chart",
    "0-99": "Hundreds Chart, 0 to 99",
  },
  rangeLabel: "Numbers on the chart",
  ranges: {
    "1-100": "1 to 100",
    "1-120": "1 to 120",
    "0-99": "0 to 99",
  },
  fillLabel: "Numbers shown",
  fills: {
    complete: "All numbers — a complete chart",
    q10: "Hide about 10% — find the missing numbers",
    q20: "Hide about 20% — find the missing numbers",
    q30: "Hide about 30% — find the missing numbers",
    q50: "Hide half — find the missing numbers",
    blank: "Hide every number — a blank chart",
  },
  skipLabel: "Shade a skip-counting pattern",
  skipNone: "No shading",
  skipOption: "Count by {step}",
  skipModeLabel: "Which numbers are shaded",
  skipModes: {
    multiples: "Multiples of the number (for 5: 5, 10, 15 …)",
    "from-start": "Count on from the first number (for 5: 1, 6, 11 …)",
  },
  chartLabel: "Number chart, {range}",
} as const;

type Fill = keyof typeof HUNDREDS_CHART_STRINGS.fills;

const FILL_FRACTION: Record<Fill, number> = {
  complete: 0,
  q10: 0.1,
  q20: 0.2,
  q30: 0.3,
  q50: 0.5,
  blank: 1,
};
const RANGES: HundredsRange[] = ["1-100", "1-120", "0-99"];
const STEPS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export default function HundredsChartWidget() {
  const strings = useToolUi(HUNDREDS_CHART_STRINGS);
  const [settings, update] = useCommonSettings(
    defaultSettings({ header: false, answerKey: true })
  );
  const seed = useWorksheetSeed();
  const [range, setRange] = useState<HundredsRange>("1-100");
  const [fill, setFill] = useState<Fill>("complete");
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<SkipMode>("multiples");

  const numbers = hundredsNumbers(range);
  const fraction = FILL_FRACTION[fill];
  const hasGaps = fraction > 0;
  const usesSeed = fraction > 0 && fraction < 1;
  const shaded = skipShaded(numbers, step, mode);
  const blanks = hasGaps
    ? blankNumbers(numbers, fraction, createRng(seed.seed))
    : null;
  const label = t(strings.chartLabel, { range: strings.ranges[range] });
  const grid = (shown: ReadonlySet<number> | null) => (
    <HundredsGrid
      blanks={shown}
      fontPoints={FONT_POINTS[settings.fontSize]}
      label={label}
      numbers={numbers}
      shaded={shaded}
    />
  );

  return (
    <div className="space-y-6 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            id="hc-range"
            label={strings.rangeLabel}
            onChange={(value: HundredsRange) => setRange(value)}
            options={RANGES.map((value) => ({
              value,
              label: strings.ranges[value],
            }))}
            value={range}
          />
          <SelectField
            id="hc-fill"
            label={strings.fillLabel}
            onChange={(value: Fill) => setFill(value)}
            options={(Object.keys(FILL_FRACTION) as Fill[]).map((value) => ({
              value,
              label: strings.fills[value],
            }))}
            value={fill}
          />
          <SelectField
            id="hc-skip"
            label={strings.skipLabel}
            onChange={(value) => setStep(Number(value))}
            options={[
              { value: "0", label: strings.skipNone },
              ...STEPS.map((value) => ({
                value: String(value),
                label: t(strings.skipOption, { step: String(value) }),
              })),
            ]}
            value={String(step)}
          />
          {step > 0 ? (
            <SelectField
              id="hc-skip-mode"
              label={strings.skipModeLabel}
              onChange={(value: SkipMode) => setMode(value)}
              options={(["multiples", "from-start"] as const).map((value) => ({
                value,
                label: strings.skipModes[value],
              }))}
              value={mode}
            />
          ) : null}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="hc-font"
            label={strings.fontSizeLabel}
            onChange={(fontSize) => update({ fontSize })}
            options={(["small", "medium", "large"] as const).map((value) => ({
              value,
              label: strings.fontSizes[value],
            }))}
            value={settings.fontSize}
          />
          <SelectField
            id="hc-paper"
            label={strings.paperLabel}
            onChange={(paper) => update({ paper })}
            options={(["letter", "a4"] as const).map((value) => ({
              value,
              label: strings.papers[value],
            }))}
            value={settings.paper}
          />
        </div>
        <TextField
          hint={strings.titleHint}
          id="hc-title"
          label={strings.titleLabel}
          onChange={(title) => update({ title })}
          value={settings.title}
        />
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={settings.header}
            label={strings.showHeader}
            onChange={(header) => update({ header })}
          />
          {hasGaps ? (
            <Checkbox
              checked={settings.answerKey}
              label={strings.answerKey}
              onChange={(answerKey) => update({ answerKey })}
            />
          ) : null}
        </div>
        <ActionBar
          idPrefix="hc"
          seed={seed}
          showSeed={usesSeed}
          strings={strings}
        />
      </div>
      <WorksheetSheets
        answers={grid(null)}
        problems={grid(blanks)}
        seed={seed.seed}
        settings={{ ...settings, answerKey: settings.answerKey && hasGaps }}
        showSeed={usesSeed}
        strings={strings}
        title={settings.title.trim() || strings.titles[range]}
      />
    </div>
  );
}
